import type {
  DataExplorerAppearance,
  DataExplorerBackground,
  DataExplorerBorder,
  DataExplorerControlTone,
  DataExplorerDensity,
  DataExplorerRadius,
  DataExplorerResolvedTheme,
  DataExplorerSurface,
  DataExplorerThemeStyles,
  DataExplorerThemePresetId,
  DataExplorerThemeSlots,
} from './types';
import { cn } from './utils';

type ResolvedSlots = Required<DataExplorerThemeSlots>;

const emptyStyles: DataExplorerThemeStyles = {};

const baseSlots: ResolvedSlots = {
  surfaceRoot:
    'relative flex h-full min-h-0 w-full min-w-0 overflow-hidden rounded-md border border-border bg-background shadow-sm',
  toolbarRoot:
    'flex flex-wrap items-center gap-2 border-b border-border bg-background px-4 py-3',
  toolbarLeading: 'flex items-center gap-2',
  toolbarSearch:
    'flex h-10 min-w-52 flex-1 items-center gap-2 rounded-md border border-border bg-background px-3 text-sm',
  toolbarSearchIcon: 'inline-flex shrink-0 text-muted-foreground',
  toolbarSearchInput:
    'min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground',
  toolbarViewSwitch:
    'flex items-center rounded-md border border-border bg-background p-0.5',
  toolbarViewButton:
    'inline-flex h-9 min-w-9 items-center justify-center gap-2 rounded-md px-2 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-50',
  toolbarViewButtonActive: 'bg-muted text-foreground',
  toolbarActions: 'flex items-center gap-2',
  actionBarRoot: 'flex min-w-0 flex-wrap items-center gap-2',
  actionBarLeading: 'flex shrink-0 items-center gap-2',
  actionBarActions: 'flex min-w-0 flex-1 flex-wrap items-center gap-2',
  actionBarTrailing: 'flex shrink-0 items-center gap-2',
  actionButtonBase:
    'inline-flex h-9 items-center justify-center gap-2 rounded-md border px-3 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
  actionButtonDefault:
    'border-border bg-background text-foreground hover:bg-accent/60',
  actionButtonPrimary:
    'border-primary/20 bg-primary/10 text-primary hover:bg-primary/15',
  actionButtonDanger:
    'border-destructive/20 bg-destructive/10 text-destructive-text hover:bg-destructive/15',
  actionButtonMuted:
    'border-border/60 bg-muted/45 text-muted-foreground hover:bg-muted/70 hover:text-foreground',
  actionButtonActive: 'ring-1 ring-primary/30',
  actionButtonDisabled: 'cursor-not-allowed opacity-60',
  actionIconButton: 'w-9 px-0',
  actionToggleActive: 'border-primary/30 bg-primary/10 text-primary',
  controlButton:
    'inline-flex h-10 min-w-0 items-center justify-center gap-2 rounded-md border border-border bg-background px-3 text-sm font-medium text-foreground transition-colors hover:bg-accent/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60',
  controlButtonIcon: 'inline-flex h-4 w-4 shrink-0 items-center justify-center',
  controlButtonLabel: 'min-w-0 truncate',
  controlSelectTrigger:
    'inline-flex h-10 min-w-28 items-center justify-between gap-3 rounded-md border border-border bg-background px-3 text-sm font-medium text-foreground transition-colors hover:bg-accent/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60',
  controlSelectValue: 'flex min-w-0 items-center gap-1 truncate',
  controlCheckbox:
    'inline-flex h-9 items-center gap-2 rounded-md border border-border bg-background px-3 text-sm font-medium text-foreground transition-colors hover:bg-accent/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60',
  controlCheckboxIndicator:
    'flex h-4 w-4 shrink-0 items-center justify-center rounded border border-border bg-background text-primary',
  controlCheckboxLabel: 'min-w-0 truncate',
  menuTrigger:
    'inline-flex h-8 w-8 items-center justify-center rounded-md text-foreground transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
  menuContent:
    'z-50 min-w-36 rounded-md border border-border bg-popover p-1 text-popover-foreground shadow-md outline-none',
  menuLabel: 'px-2 py-1.5 text-xs font-medium text-muted-foreground',
  menuSeparator: '-mx-1 my-1 h-px bg-border',
  menuItem:
    'relative flex cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
  menuItemPrimary: 'text-primary focus:text-primary',
  menuItemDanger: 'text-destructive-text focus:text-destructive-text',
  menuItemMuted: 'text-muted-foreground',
  menuItemIcon: 'inline-flex h-4 w-4 shrink-0 items-center justify-center',
  menuItemLabel: 'truncate',
  cardRoot:
    'group relative flex min-w-0 flex-col overflow-hidden rounded-lg border border-border/70 bg-background transition-shadow',
  cardInteractive: 'cursor-pointer hover:shadow-sm',
  cardSelected: 'ring-2 ring-primary',
  cardDisabled: 'opacity-60',
  cardHeader: 'p-4',
  cardMedia: 'shrink-0 overflow-hidden',
  cardFooter: 'shrink-0 border-t border-border/60 px-4 py-3',
  recordCardRoot: 'group relative border-border/70 transition-shadow hover:shadow-sm',
  recordCardActive: 'z-10 ring-2 ring-monkeys-500',
  footerRoot: 'flex items-center border-t border-border bg-background px-4 py-3',
  footerSummary:
    'flex items-center gap-1 whitespace-nowrap text-sm text-muted-foreground',
  footerJumpButton:
    'inline-flex items-center gap-1 text-muted-foreground underline-offset-4 hover:text-foreground hover:underline',
  footerPageSizeSelect:
    'h-8 rounded-md border border-border bg-background px-2 text-sm text-foreground',
  previewRoot:
    'relative h-full min-h-0 w-full flex-1 select-none self-stretch overflow-hidden bg-slate-950',
  previewPlaceholder:
    'flex h-full w-full items-center justify-center rounded-xl bg-white/5 px-6 text-center text-sm text-white/60',
  previewActions: 'absolute right-4 top-4 z-20 flex flex-col gap-2',
  previewControls: 'absolute bottom-4 right-4 z-20 flex flex-col gap-2',
  previewZoomLabel:
    'min-w-[3rem] rounded-full border border-white/15 bg-black/30 px-1 py-1 text-center text-xs font-medium text-white/70 shadow-lg backdrop-blur',
  previewControlButton:
    'inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/15 bg-black/30 text-white/90 shadow-lg backdrop-blur transition hover:bg-black/45',
  previewNavigationButton:
    'absolute top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-white/15 bg-black/30 text-white/90 shadow-lg backdrop-blur transition-colors hover:bg-black/45 disabled:pointer-events-none disabled:opacity-40',
  detailRoot: 'flex h-full flex-col overflow-hidden bg-background',
  detailHeader:
    'flex items-center justify-between gap-3 border-b border-border bg-background px-4 py-4 sm:px-5',
  detailLeading:
    'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground',
  detailNavigation:
    'hidden items-center gap-1 rounded-lg border border-border bg-background px-1 py-1 md:flex',
  detailNavigationButton:
    'flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:pointer-events-none disabled:opacity-50',
  detailCloseButton:
    'flex h-10 w-10 items-center justify-center rounded-full border border-border bg-background text-muted-foreground transition-colors hover:bg-accent hover:text-foreground',
  detailPreviewPanel:
    'min-h-0 overflow-hidden border-b border-border bg-background xl:border-b-0',
  detailPreviewFrame:
    'relative h-full min-h-0 w-full overflow-hidden bg-background',
  detailRightPanel:
    'min-h-0 min-w-0 border-t border-border bg-background xl:border-l xl:border-t-0',
  detailRightPanelBody:
    'h-full overflow-y-auto overscroll-contain bg-background p-5',
  treeShellRoot: 'flex h-full min-h-0 flex-col overflow-hidden',
  treeShellHeader:
    'flex shrink-0 items-start justify-between gap-3 px-4 py-3',
  treeShellTitle: 'truncate text-sm font-semibold',
  treeShellSubtitle: 'mt-1 text-sm text-muted-foreground',
  treeShellToolbar: 'flex shrink-0 items-center gap-2',
  treeShellBody: 'min-h-0 flex-1 overflow-hidden',
  treeShellFooter: 'shrink-0',
  treeRoot: 'min-w-0 space-y-1 overflow-hidden text-sm',
  treeGroup: 'min-w-0 space-y-1',
  treeGroupHeader: 'group/tree-group flex min-w-0 items-center gap-1',
  treeGroupHeaderButton:
    'flex h-9 min-w-0 flex-1 items-center gap-2 rounded-md px-2 text-left font-medium text-muted-foreground transition-colors hover:bg-accent/55 hover:text-foreground',
  treeGroupContent: 'min-w-0 space-y-0.5',
  treeGroupActions:
    'shrink-0 opacity-0 transition-opacity group-hover/tree-group:opacity-100 data-[state=open]:opacity-100',
  treeNode: 'min-w-0',
  treeNodeButton:
    'group/tree-node flex min-h-9 w-full min-w-0 items-center gap-1 rounded-md pr-1 text-left text-sm text-foreground transition-colors hover:bg-accent/55 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
  treeNodeSelected: 'bg-accent text-accent-foreground',
  treeNodeDisabled: 'cursor-not-allowed opacity-55',
  treeNodeDragging: 'opacity-50',
  treeNodeToggle:
    'inline-flex h-5 w-5 shrink-0 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-accent hover:text-foreground',
  treeNodeIcon:
    'inline-flex h-4 w-4 shrink-0 items-center justify-center text-muted-foreground group-hover/tree-node:text-foreground',
  treeNodeLabel: 'min-w-0 truncate',
  treeNodeDescription: 'mt-0.5 truncate text-xs text-muted-foreground',
  treeNodeMeta: 'ml-2 shrink-0 text-xs text-muted-foreground',
  treeNodeBadge:
    'ml-2 shrink-0 rounded-full border border-border bg-muted px-1.5 py-0.5 text-[11px] leading-none text-muted-foreground',
  treeNodeActions:
    'ml-1 shrink-0 opacity-0 transition-opacity group-hover/tree-node:opacity-100 data-[state=open]:opacity-100',
  treeNodeChildren: 'mt-0.5 min-w-0 space-y-0.5',
  treeNodeDragHandle:
    'inline-flex h-5 w-5 shrink-0 cursor-grab items-center justify-center rounded text-muted-foreground opacity-0 transition-opacity hover:bg-accent hover:text-foreground active:cursor-grabbing group-hover/tree-node:opacity-100',
  treeEmpty: 'px-3 py-2 text-xs text-muted-foreground',
  treeError: 'px-3 py-2 text-xs text-destructive-text',
  treeLoading: 'px-3 py-2 text-xs text-muted-foreground',
};

const surfaceSlots: Record<DataExplorerSurface, Partial<DataExplorerThemeSlots>> = {
  card: {},
  flush: {
    surfaceRoot:
      'relative flex h-full min-h-0 w-full min-w-0 overflow-hidden',
  },
};

const densitySlots: Record<DataExplorerDensity, Partial<DataExplorerThemeSlots>> = {
  compact: {
    treeShellHeader: 'px-3 py-2',
    toolbarRoot: 'px-3 py-2',
    toolbarSearch: 'h-9 min-w-48',
    toolbarViewButton: 'h-8 min-w-8 text-xs',
    actionButtonBase: 'h-8 px-2.5 text-xs',
    actionIconButton: 'h-8 w-8 px-0',
    controlButton: 'h-9 px-2.5 text-xs',
    controlSelectTrigger: 'h-9 min-w-24 px-2.5 text-xs',
    controlCheckbox: 'h-8 px-2.5 text-xs',
    menuTrigger: 'h-8 w-8',
    footerRoot: 'px-3 py-2',
    footerPageSizeSelect: 'h-8 text-xs',
    treeGroupHeaderButton: 'h-8 text-xs',
    treeNodeButton: 'min-h-8 text-xs',
    treeEmpty: 'py-1.5 text-xs',
    treeError: 'py-1.5 text-xs',
    treeLoading: 'py-1.5 text-xs',
  },
  default: {},
  comfortable: {
    treeShellHeader: 'px-5 py-4',
    toolbarRoot: 'px-5 py-4',
    toolbarSearch: 'h-11',
    toolbarViewButton: 'h-10 min-w-10',
    actionButtonBase: 'h-10 px-3.5',
    actionIconButton: 'h-10 w-10 px-0',
    controlButton: 'h-11 px-3.5',
    controlSelectTrigger: 'h-11 min-w-32 px-3.5',
    controlCheckbox: 'h-10 px-3.5',
    menuTrigger: 'h-9 w-9',
    footerRoot: 'px-5 py-3.5',
    treeGroupHeaderButton: 'h-10',
    treeNodeButton: 'min-h-10',
  },
};

const radiusSlots: Record<DataExplorerRadius, Partial<DataExplorerThemeSlots>> = {
  none: {
    surfaceRoot: 'rounded-none',
    toolbarSearch: 'rounded-none',
    toolbarViewSwitch: 'rounded-none',
    toolbarViewButton: 'rounded-none',
    actionButtonBase: 'rounded-none',
    controlButton: 'rounded-none',
    controlSelectTrigger: 'rounded-none',
    controlCheckbox: 'rounded-none',
    controlCheckboxIndicator: 'rounded-none',
    menuTrigger: 'rounded-none',
    menuContent: 'rounded-none',
    menuItem: 'rounded-none',
    cardRoot: 'rounded-none',
    footerPageSizeSelect: 'rounded-none',
    detailLeading: 'rounded-none',
    detailNavigation: 'rounded-none',
    detailNavigationButton: 'rounded-none',
    detailCloseButton: 'rounded-none',
    treeGroupHeaderButton: 'rounded-none',
    treeNodeButton: 'rounded-none',
    treeNodeToggle: 'rounded-none',
    treeNodeBadge: 'rounded-none',
    treeNodeDragHandle: 'rounded-none',
  },
  sm: {
    surfaceRoot: 'rounded-sm',
    toolbarSearch: 'rounded-sm',
    toolbarViewSwitch: 'rounded-sm',
    toolbarViewButton: 'rounded-sm',
    actionButtonBase: 'rounded-sm',
    controlButton: 'rounded-sm',
    controlSelectTrigger: 'rounded-sm',
    controlCheckbox: 'rounded-sm',
    controlCheckboxIndicator: 'rounded-sm',
    menuTrigger: 'rounded-sm',
    menuContent: 'rounded-sm',
    menuItem: 'rounded-sm',
    cardRoot: 'rounded-sm',
    treeGroupHeaderButton: 'rounded-sm',
    treeNodeButton: 'rounded-sm',
    treeNodeToggle: 'rounded-sm',
    treeNodeBadge: 'rounded-sm',
    treeNodeDragHandle: 'rounded-sm',
  },
  md: {},
  lg: {
    surfaceRoot: 'rounded-xl',
    toolbarSearch: 'rounded-lg',
    toolbarViewSwitch: 'rounded-lg',
    toolbarViewButton: 'rounded-lg',
    actionButtonBase: 'rounded-lg',
    controlButton: 'rounded-lg',
    controlSelectTrigger: 'rounded-lg',
    controlCheckbox: 'rounded-lg',
    controlCheckboxIndicator: 'rounded-md',
    menuTrigger: 'rounded-lg',
    menuContent: 'rounded-lg',
    cardRoot: 'rounded-xl',
    treeGroupHeaderButton: 'rounded-lg',
    treeNodeButton: 'rounded-lg',
    treeNodeToggle: 'rounded-md',
    treeNodeBadge: 'rounded-lg',
    treeNodeDragHandle: 'rounded-md',
  },
  xl: {
    surfaceRoot: 'rounded-2xl',
    toolbarSearch: 'rounded-xl',
    toolbarViewSwitch: 'rounded-xl',
    toolbarViewButton: 'rounded-xl',
    actionButtonBase: 'rounded-xl',
    controlButton: 'rounded-xl',
    controlSelectTrigger: 'rounded-xl',
    controlCheckbox: 'rounded-xl',
    controlCheckboxIndicator: 'rounded-lg',
    menuTrigger: 'rounded-xl',
    menuContent: 'rounded-xl',
    cardRoot: 'rounded-2xl',
    treeGroupHeaderButton: 'rounded-xl',
    treeNodeButton: 'rounded-xl',
    treeNodeToggle: 'rounded-lg',
    treeNodeBadge: 'rounded-xl',
    treeNodeDragHandle: 'rounded-lg',
  },
  full: {
    surfaceRoot: 'rounded-2xl',
    toolbarSearch: 'rounded-full',
    toolbarViewSwitch: 'rounded-full',
    toolbarViewButton: 'rounded-full',
    actionButtonBase: 'rounded-full',
    controlButton: 'rounded-full',
    controlSelectTrigger: 'rounded-full',
    controlCheckbox: 'rounded-full',
    controlCheckboxIndicator: 'rounded-full',
    menuTrigger: 'rounded-full',
    cardRoot: 'rounded-2xl',
    treeGroupHeaderButton: 'rounded-full',
    treeNodeButton: 'rounded-full',
    treeNodeToggle: 'rounded-full',
    treeNodeBadge: 'rounded-full',
    treeNodeDragHandle: 'rounded-full',
  },
};

const borderSlots: Record<DataExplorerBorder, Partial<DataExplorerThemeSlots>> = {
  none: {
    surfaceRoot: 'border-transparent',
    toolbarRoot: 'border-transparent',
    toolbarSearch: 'border-transparent',
    toolbarViewSwitch: 'border-transparent',
    actionButtonBase: 'border-transparent',
    controlButton: 'border-transparent',
    controlSelectTrigger: 'border-transparent',
    controlCheckbox: 'border-transparent',
    cardRoot: 'border-transparent',
    footerRoot: 'border-transparent',
    detailHeader: 'border-transparent',
    detailPreviewPanel: 'border-transparent',
    detailRightPanel: 'border-transparent',
  },
  subtle: {
    surfaceRoot: 'border-border/60',
    toolbarRoot: 'border-border/60',
    toolbarSearch: 'border-border/60',
    toolbarViewSwitch: 'border-border/60',
    actionButtonDefault: 'border-border/60',
    controlButton: 'border-border/60',
    controlSelectTrigger: 'border-border/60',
    controlCheckbox: 'border-border/60',
    cardRoot: 'border-border/60',
    footerRoot: 'border-border/60',
  },
  solid: {},
  strong: {
    surfaceRoot: 'border-foreground/20',
    toolbarRoot: 'border-foreground/20',
    toolbarSearch: 'border-foreground/20',
    toolbarViewSwitch: 'border-foreground/20',
    actionButtonDefault: 'border-foreground/20',
    controlButton: 'border-foreground/20',
    controlSelectTrigger: 'border-foreground/20',
    controlCheckbox: 'border-foreground/20',
    cardRoot: 'border-foreground/20',
    footerRoot: 'border-foreground/20',
  },
};

const backgroundSlots: Record<DataExplorerBackground, Partial<DataExplorerThemeSlots>> = {
  plain: {},
  muted: {
    surfaceRoot: 'bg-card',
    toolbarRoot: 'bg-muted/20',
    toolbarSearch: 'bg-background',
    toolbarViewSwitch: 'bg-background',
    controlButton: 'bg-background',
    controlSelectTrigger: 'bg-background',
    controlCheckbox: 'bg-background',
    cardRoot: 'bg-card',
    footerRoot: 'bg-muted/20',
    detailHeader: 'bg-muted/20',
  },
  soft: {
    surfaceRoot: 'bg-background/95 shadow-sm',
    toolbarRoot: 'bg-background/95',
    actionButtonDefault: 'bg-muted/30 hover:bg-muted/55',
    controlButton: 'bg-muted/30 hover:bg-muted/55',
    controlSelectTrigger: 'bg-muted/30 hover:bg-muted/55',
    controlCheckbox: 'bg-muted/30 hover:bg-muted/55',
    cardRoot: 'bg-background/95 shadow-sm',
    footerRoot: 'bg-background/95',
  },
  glass: {
    surfaceRoot: 'border-white/10 bg-white/[0.06] shadow-none backdrop-blur-md',
    toolbarRoot: 'border-white/10 bg-white/5 backdrop-blur-md',
    toolbarSearch: 'border-white/10 bg-white/5',
    toolbarViewSwitch: 'border-white/10 bg-white/5',
    actionButtonDefault:
      'border-white/10 bg-white/[0.08] text-foreground hover:bg-white/[0.14]',
    controlButton:
      'border-white/10 bg-white/[0.08] text-foreground hover:bg-white/[0.14]',
    controlSelectTrigger:
      'border-white/10 bg-white/[0.08] text-foreground hover:bg-white/[0.14]',
    controlCheckbox:
      'border-white/10 bg-white/[0.08] text-foreground hover:bg-white/[0.14]',
    controlCheckboxIndicator: 'border-white/15 bg-white/10',
    cardRoot: 'border-white/10 bg-white/[0.06] shadow-none',
    footerRoot: 'border-white/10 bg-white/5 backdrop-blur-md',
  },
  'dark-stage': {
    previewRoot: 'bg-slate-950',
    previewPlaceholder: 'bg-white/5 text-white/60',
    actionButtonBase:
      'inline-flex h-9 items-center justify-center gap-2 rounded-full border px-3 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/45 focus-visible:ring-offset-0',
    actionButtonDefault:
      'border-white/15 bg-black/30 text-white/90 shadow-lg backdrop-blur hover:bg-black/[0.45]',
    controlButton:
      'border-white/15 bg-black/30 text-white/90 shadow-lg backdrop-blur hover:bg-black/[0.45]',
    controlSelectTrigger:
      'border-white/15 bg-black/30 text-white/90 shadow-lg backdrop-blur hover:bg-black/[0.45]',
    controlCheckbox:
      'border-white/15 bg-black/30 text-white/90 shadow-lg backdrop-blur hover:bg-black/[0.45]',
    actionButtonPrimary:
      'border-white/25 bg-white/20 text-white shadow-lg backdrop-blur hover:bg-white/28',
    actionButtonMuted:
      'border-white/10 bg-black/20 text-white/70 shadow-lg backdrop-blur hover:bg-black/[0.35] hover:text-white',
    actionIconButton: 'h-9 w-9 px-0',
  },
};

const presetDefaults: Record<
  DataExplorerThemePresetId,
  Pick<DataExplorerAppearance, 'density' | 'radius' | 'border' | 'background'>
> = {
  default: {
    density: 'default',
    radius: 'md',
    border: 'solid',
    background: 'plain',
  },
  soft: {
    density: 'default',
    radius: 'md',
    border: 'subtle',
    background: 'plain',
  },
  compact: {
    density: 'compact',
    radius: 'md',
    border: 'subtle',
    background: 'soft',
  },
  'tinted-glass': {
    density: 'default',
    radius: 'lg',
    border: 'subtle',
    background: 'glass',
  },
  flat: {
    density: 'compact',
    radius: 'none',
    border: 'solid',
    background: 'plain',
  },
  glass: {
    density: 'default',
    radius: 'lg',
    border: 'subtle',
    background: 'glass',
  },
  'hard-edge': {
    density: 'compact',
    radius: 'none',
    border: 'solid',
    background: 'plain',
  },
};

const presetSlots: Record<string, Partial<DataExplorerThemeSlots>> = {
  soft: {
    actionButtonBase: 'font-medium',
    controlButton: 'font-medium',
    controlSelectTrigger: 'font-medium',
    controlCheckbox: 'font-medium',
    menuTrigger: 'rounded-full',
    cardRoot: 'border-border/70',
  },
  compact: {
    toolbarRoot: 'gap-2',
    actionButtonBase: 'text-xs',
    controlButton: 'text-xs',
    controlSelectTrigger: 'text-xs',
    controlCheckbox: 'text-xs',
    footerRoot: 'text-xs',
  },
  'tinted-glass': {
    actionButtonDefault:
      'border-border/60 bg-background/10 text-foreground hover:bg-accent/60',
    actionButtonPrimary:
      'border-primary/30 bg-primary/15 text-primary-foreground hover:bg-primary/20',
    actionButtonMuted:
      'border-border/50 bg-muted/20 text-muted-foreground hover:bg-muted/45 hover:text-foreground',
    controlButton:
      'border-border/60 bg-background/10 text-foreground hover:bg-accent/60',
    controlSelectTrigger:
      'border-border/60 bg-background/10 text-foreground hover:bg-accent/60',
    controlCheckbox:
      'border-border/60 bg-background/10 text-foreground hover:bg-accent/60',
    controlCheckboxIndicator: 'border-border/60 bg-background/10 text-primary',
    menuContent: 'border-border bg-popover text-popover-foreground shadow-2xl',
    menuSeparator: 'bg-border',
    menuItem: 'focus:bg-accent focus:text-accent-foreground',
    cardSelected: 'ring-2 ring-primary',
    previewRoot: 'bg-background',
    treeGroupHeaderButton:
      'text-muted-foreground hover:bg-accent/60 hover:text-foreground',
    treeNodeButton:
      'text-foreground hover:bg-accent/60 hover:text-accent-foreground focus-visible:ring-ring',
    treeNodeSelected: 'bg-accent text-accent-foreground',
    treeNodeToggle: 'text-muted-foreground hover:bg-accent hover:text-foreground',
    treeNodeIcon:
      'text-muted-foreground group-hover/tree-node:text-foreground',
    treeNodeMeta: 'text-muted-foreground',
    treeNodeBadge: 'border-border bg-muted text-muted-foreground',
    treeNodeActions: 'text-muted-foreground',
    treeNodeDragHandle:
      'text-muted-foreground hover:bg-accent hover:text-foreground group-hover/tree-node:opacity-100',
    treeEmpty: 'text-muted-foreground',
    treeError: 'text-destructive-text',
    treeLoading: 'text-muted-foreground',
  },
  flat: {
    toolbarRoot: 'border-slate-200 bg-white',
    toolbarSearch: 'rounded-none border-slate-200 bg-white shadow-none',
    toolbarViewSwitch: 'rounded-none border-slate-200 bg-white shadow-none',
    actionButtonBase: 'rounded-none shadow-none',
    controlButton: 'rounded-none border-slate-200 bg-white text-slate-700 shadow-none hover:bg-slate-50',
    controlSelectTrigger:
      'rounded-none border-slate-200 bg-white text-slate-700 shadow-none hover:bg-slate-50',
    controlCheckbox:
      'rounded-none border-slate-200 bg-white text-slate-700 shadow-none hover:bg-slate-50',
    actionButtonDefault:
      'border-slate-200 bg-white text-slate-700 hover:bg-slate-50',
    menuTrigger:
      'rounded-none border border-slate-200 bg-white text-slate-700 shadow-none hover:bg-slate-50',
    menuContent: 'rounded-none border-slate-200 bg-white shadow-none',
    cardRoot: 'rounded-none border-slate-200 bg-white shadow-none',
    footerRoot: 'border-slate-200 bg-white',
    treeRoot: 'space-y-1 text-sm',
    treeGroupHeader: 'group/tree-group flex min-w-0 items-center',
    treeGroupHeaderButton:
      'h-9 rounded-none px-3 text-slate-600 shadow-none hover:bg-slate-100 hover:text-slate-950',
    treeGroupContent: 'space-y-1',
    treeNodeButton:
      'min-h-9 rounded-none text-slate-700 hover:bg-slate-100 hover:text-slate-950 focus-visible:ring-slate-300',
    treeNodeSelected: 'bg-slate-100 text-slate-950',
    treeNodeToggle:
      'rounded-none text-slate-500 hover:bg-transparent hover:text-slate-950',
    treeNodeIcon: 'text-slate-500 group-hover/tree-node:text-slate-950',
    treeNodeDragHandle:
      'rounded-none text-slate-400 hover:bg-transparent hover:text-slate-700',
    treeNodeActions: 'text-slate-500',
  },
  glass: {
    menuContent: 'border-white/10 bg-slate-950/90 text-white backdrop-blur-xl',
    menuSeparator: 'bg-white/10',
    menuItem: 'focus:bg-white/10 focus:text-white',
  },
  'hard-edge': {
    toolbarRoot: 'border-slate-200 bg-white',
    actionButtonBase: 'rounded-none shadow-none',
    controlButton: 'rounded-none shadow-none',
    controlSelectTrigger: 'rounded-none shadow-none',
    controlCheckbox: 'rounded-none shadow-none',
    cardRoot: 'rounded-none border-slate-200 bg-white shadow-none',
    footerRoot: 'border-slate-200 bg-white',
    footerPageSizeSelect: 'rounded-none border-slate-200 shadow-none',
  },
};

const slotKeys = Object.keys(baseSlots) as Array<keyof DataExplorerThemeSlots>;

function mergeSlots(
  ...sources: Array<Partial<DataExplorerThemeSlots> | undefined>
): ResolvedSlots {
  return slotKeys.reduce((resolved, key) => {
    resolved[key] = cn(...sources.map((source) => source?.[key]));
    return resolved;
  }, {} as ResolvedSlots);
}

function mergeStyles(
  ...sources: Array<DataExplorerThemeStyles | undefined>
): DataExplorerThemeStyles {
  return sources.reduce<DataExplorerThemeStyles>(
    (resolved, source) => ({
      ...resolved,
      ...source,
    }),
    {},
  );
}

function getPresetDefaults(preset: DataExplorerThemePresetId) {
  const defaults = presetDefaults[preset as keyof typeof presetDefaults];
  if (!defaults) {
    throw new TypeError(
      `Unknown Data Explorer theme preset "${String(preset)}". Expected one of: ${Object.keys(presetDefaults).join(', ')}.`,
    );
  }
  return defaults;
}

export function resolveDataExplorerAppearance(
  appearance?: DataExplorerAppearance,
): DataExplorerResolvedTheme {
  const preset = appearance?.preset ?? 'default';
  const surface = appearance?.surface ?? 'card';
  const defaults = getPresetDefaults(preset);
  const density = appearance?.density ?? defaults.density ?? 'default';
  const radius = appearance?.radius ?? defaults.radius ?? 'md';
  const border = appearance?.border ?? defaults.border ?? 'solid';
  const background = appearance?.background ?? defaults.background ?? 'plain';

  const slots = mergeSlots(
    baseSlots,
    presetSlots[preset],
    densitySlots[density],
    radiusSlots[radius],
    borderSlots[border],
    backgroundSlots[background],
    appearance?.slots,
  );
  if (surface === 'flush') {
    slots.surfaceRoot = cn(
      surfaceSlots.flush.surfaceRoot,
      appearance?.slots?.surfaceRoot,
    );
  }

  return {
    surface,
    preset,
    density,
    radius,
    border,
    background,
    styles: mergeStyles(emptyStyles, appearance?.styles),
    slots,
  };
}

export function getDataExplorerActionToneClassName(
  theme: DataExplorerResolvedTheme,
  tone: DataExplorerControlTone | undefined,
  active: boolean,
) {
  return cn(
    tone === 'primary'
      ? theme.slots.actionButtonPrimary
      : tone === 'danger'
        ? theme.slots.actionButtonDanger
        : tone === 'muted'
          ? theme.slots.actionButtonMuted
          : theme.slots.actionButtonDefault,
    active && theme.slots.actionButtonActive,
  );
}

export function getDataExplorerMenuItemToneClassName(
  theme: DataExplorerResolvedTheme,
  tone: DataExplorerControlTone | undefined,
) {
  if (tone === 'primary') return theme.slots.menuItemPrimary;
  if (tone === 'danger') return theme.slots.menuItemDanger;
  if (tone === 'muted') return theme.slots.menuItemMuted;
  return undefined;
}
